import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, UserPlus, ArrowLeft, AlertCircle, CheckCircle, Mail } from '../../components/icons';
import { supabase } from '../../lib/supabase';
import { isUserAdmin } from '../../lib/auth';

interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string | null;
}

export function AdminManagement() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate('/login'); return; }
    const admin = await isUserAdmin(user.id);
    if (!admin) { navigate('/login'); return; }
    await loadAdmins();
    setIsLoading(false);
  };

  const loadAdmins = async () => {
    const { data, error: queryError } = await supabase
      .from('users')
      .select('id, email, full_name, role, created_at')
      .eq('role', 'admin')
      .order('created_at', { ascending: true });
    if (queryError) { setError(queryError.message); return; }
    setAdmins(data || []);
  };

  const handleCreate = async () => {
    setError('');
    setSuccess('');

    if (!email.trim() || !password.trim() || !fullName.trim()) {
      setError('All fields are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: result, error: fnError } = await supabase.functions.invoke('create-admin', {
        body: { email: email.trim(), password, fullName: fullName.trim() },
      });

      if (fnError) {
        let errMsg = 'Failed to create admin account';
        try {
          const errBody = await (fnError as any).context?.json?.();
          if (errBody?.error) errMsg = errBody.error;
        } catch {
          if (result?.error) errMsg = result.error;
        }
        throw new Error(errMsg);
      }

      setSuccess(`Admin account created for ${email.trim()}. They can now sign in at /login.`);
      setEmail('');
      setPassword('');
      setFullName('');
      setShowForm(false);
      await loadAdmins();
    } catch (err: any) {
      setError(err.message || 'Failed to create admin account.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <p className="text-gray-40">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-10 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-60" />
            </button>
            <div>
              <h1 className="text-heading-xs text-gray-80">Admin Management</h1>
              <p className="text-text-sm text-gray-40">Create and manage admin accounts</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-90 text-white rounded-xl text-sm font-semibold hover:bg-brand-80 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            {showForm ? 'Cancel' : 'Add Admin'}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl"
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{success}</p>
          </motion.div>
        )}

        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="freud-card-elevated p-6 mb-8"
          >
            <h2 className="text-text-lg text-gray-80 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand-60" />
              Create New Admin Account
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-70 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-80 focus:outline-none focus:ring-2 focus:ring-brand-40"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-70 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="w-full px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-80 focus:outline-none focus:ring-2 focus:ring-brand-40"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-70 mb-1.5">Temporary Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-80 focus:outline-none focus:ring-2 focus:ring-brand-40"
                />
                <p className="text-xs text-gray-40 mt-1">Share this password with the new admin securely. They can change it after signing in.</p>
              </div>
              <button
                onClick={handleCreate}
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-brand-90 text-white rounded-xl font-semibold hover:bg-brand-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="w-5 h-5" />
                {isSubmitting ? 'Creating...' : 'Create Admin Account'}
              </button>
            </div>
          </motion.div>
        )}

        <div className="freud-card-elevated p-6">
          <h2 className="text-text-lg text-gray-80 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-60" />
            Current Admins ({admins.length})
          </h2>
          <div className="space-y-3">
            {admins.map((admin) => (
              <div
                key={admin.id}
                className="flex items-center gap-4 p-4 bg-brand-5 border border-gray-20 rounded-xl"
              >
                <div className="w-10 h-10 bg-brand-10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-brand-60" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-80 truncate">
                    {admin.full_name || 'Unnamed'}
                  </p>
                  <p className="text-xs text-gray-40 flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3" />
                    {admin.email}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-10 text-brand-70 rounded-lg text-xs font-medium">
                    Admin
                  </span>
                  <p className="text-xs text-gray-30 mt-1">
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            ))}
            {admins.length === 0 && (
              <p className="text-sm text-gray-40 text-center py-8">No admin accounts found.</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
